import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { activeElementsByPage } from "../contexts/GamepadProvider";
import { PageRoutes } from "../constants/routes";

type IScrollRestoreProps = {
  setActiveElement: (element?: HTMLElement) => void;
};

const scrollPositions = new Map<string, number>();
const scrollLeftPositions = new Map<
  string,
  { id: string; scrollLeft: number }
>();

const NO_SCROLL_RESTORE_PATHS: string[] = [
  PageRoutes.Game,
  // add other pages here that should always start at the top
];

const shouldSkipScrollRestore = (pathname: string) =>
  NO_SCROLL_RESTORE_PATHS.some((path) => pathname.startsWith(path));

export const useScrollRestore = ({ setActiveElement }: IScrollRestoreProps) => {
  const location = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    const element =
      document.getElementById("search-result") ||
      document.getElementById("content-scroll") ||
      document.scrollingElement;

    if (!element) return;

    const skipVerticalRestore = shouldSkipScrollRestore(location.pathname);

    // Restore scroll position and active element when navigating back (POP)
    const savedScrollTop = scrollPositions.get(location.pathname) || 0;

    const applyScrollTop = () => {
      if (element === document.scrollingElement) {
        window.scrollTo(0, savedScrollTop);
      } else {
        (element as HTMLElement).scrollTop = savedScrollTop;
      }
    };

    applyScrollTop();
    requestAnimationFrame(applyScrollTop);

    if (navigationType === "POP") {
      const savedGamepad = activeElementsByPage.get(location.pathname);
      if (savedGamepad) {
        requestAnimationFrame(() => {
          const target = document.querySelector<HTMLElement>(
            `[data-gamepad-id="${savedGamepad}"]`,
          );
          if (target) {
            setActiveElement(target);
          }
        });
      }

      const savedScrollLeft = scrollLeftPositions.get(location.pathname);
      if (savedScrollLeft) {
        requestAnimationFrame(() => {
          const container = document.getElementById(savedScrollLeft.id);
          if (container) {
            container.scrollLeft = savedScrollLeft.scrollLeft;
          }
        });
      }
    }

    const onScroll = () => {
      if (skipVerticalRestore) return;
      const scrollTop =
        element === document.scrollingElement
          ? window.scrollY
          : (element as HTMLElement).scrollTop;
      scrollPositions.set(location.pathname, scrollTop);
    };

    const onClick = (e: Event) => {
      const target = e.target as HTMLElement | null;
      const container = target?.closest<HTMLElement>('[id^="content-games-"]');
      if (container) {
        scrollLeftPositions.set(location.pathname, {
          id: container.id,
          scrollLeft: container.scrollLeft,
        });
      }
    };

    const scrollTarget =
      element === document.scrollingElement ? window : element;
    scrollTarget.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("click", onClick, true);
    return () => {
      scrollTarget.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick, true);
    };
  }, [location.pathname, navigationType, setActiveElement]);
};
